/**
 * Route copy for `src/routes/calendar` (keys: `routes.calendar.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  agendaView: {
    today: 'Today',
    tomorrow: 'Tomorrow',
    noEventsInThisPeriod: 'No events in this period.',
    scheduleSomething: '+ Schedule something',
    events: 'events',
    event: 'event',
    allDay: 'All day',
    min: 'min',
    recurring: 'Recurring',
    reschedulable: '· reschedulable',
  },
  aggregate: {
    network: 'Network',
    question: 'Question',
    tip: 'Tip',
    postV: 'Post {{v}}',
    postsUnavailable: 'Posts unavailable',
    crmRemindersUnavailable: 'CRM reminders unavailable',
    appointmentsUnavailable: 'Appointments unavailable',
    emailCampaignsUnavailable: 'Email campaigns unavailable',
    eventV: 'Event — {{v}}',
    eventsUnavailable: 'Events unavailable',
    scheduledPostsUnavailable: 'Scheduled posts unavailable',
    scheduledContent: 'Scheduled content',
    scheduledContentUnavailable: 'Scheduled content unavailable',
    typeCannotBeRescheduledType: 'Type cannot be rescheduled: {{type}}',
    typeCannotBeDeletedType: 'Type cannot be deleted: {{type}}',
    poll: 'Poll',
    post: 'Post',
  },
  calendarPage: {
    month: 'Month',
    week: 'Week',
    day: 'Day',
    agenda: 'Agenda',
    theRequestTimedOutTryAgain: 'The request timed out — try again.',
    userNotIdentified: 'User not identified.',
    couldNotLoadTheCalendar: 'Could not load the calendar.',
    previousWeek: 'Previous week',
    nextWeek: 'Next week',
    previousDay: 'Previous day',
    nextDay: 'Next day',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    reschedulingRefusedThisPostIsAlready: 'Rescheduling refused: this post is already published or locked.',
    couldNotReschedule: 'Could not reschedule.',
    meetingTitle: 'Meeting: {{title}}',
    couldNotCreateTryAgain: 'Could not create — try again.',
    calendar: 'Calendar',
    allYourScheduledActivityInOne: 'All your scheduled activity, in one place.',
    calendarView: 'Calendar view',
    today: 'Today',
    goToADate: 'Go to a date',
    search: 'Search',
    search2: 'Search…',
    exportToIcalFormat: 'Export to iCal format',
    export: 'Export',
    schedule: '+ Schedule',
    filters: 'Filters',
    analysisRecommendationsBestTimesToSchedu: 'Analysis & recommendations — best times to schedule',
    sourcesPartiallyUnavailable: 'Sources partially unavailable',
    loadingTheCalendar: 'Loading the calendar…',
    deleteThisEvent: 'Delete this event?',
    add: '+ Add',
    loading: 'Loading…',
    noEventsThisDay: 'No events this day.',
    allDay: 'All day',
    delete: 'Delete',
  },
  eventChip: {
    reviewLabel: 'Review: {{label}}',
    attachedMedia: 'Attached media',
    lengthMediaItemS: '{{length}} media item(s)',
    recurring: 'Recurring',
  },
  eventDetail: {
    diagramN: 'diagram {{n}}',
    couldNotRescheduleTryAgain: 'Could not reschedule — try again.',
    deleteThisEvent: 'Delete this event?',
    couldNotDeleteTryAgain: 'Could not delete — try again.',
    open: 'Open',
    delete: 'Delete',
    saving: 'Saving…',
    save: 'Save',
    close: 'Close',
    when: 'When',
    vMin: ' ({{v}} min)',
    allDay: ' · All day',
    channel: 'Channel',
    status: 'Status',
    review: 'Review',
    recurrence: 'Recurrence',
    recurring: 'recurring',
    plan: 'Plan',
    guest: 'Guest',
    location: 'Location',
    preview: 'Preview',
    reschedule: 'Reschedule',
    thisEventIsManagedFromIts: 'This event is managed from its source',
    openButton: ' (“Open” button).',
    awaitingApproval: 'Awaiting approval',
    approvalQueue: 'Approval queue',
    approving: 'Approving…',
    approve: 'Approve',
    reject: 'Reject…',
    rejectionReasonOptional: 'Rejection reason (optional)…',
    rejecting: 'Rejecting…',
    confirmRejection: 'Confirm rejection',
    cancel: 'Cancel',
    remindersBeforePublishing: 'Reminders before publishing',
    hide: 'Hide',
    edit: 'Edit',
    enableRemindersInApp: 'Enable reminders (in-app)',
    remindMe: 'Remind me:',
    remindersApplyToAllYourScheduled: 'Reminders apply to all your scheduled posts.',
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    advanced: 'Advanced',
    expert: 'Expert',
    master: 'Master',
    imageGeneratedAndAttached: 'Image generated and attached.',
    gifGeneratedAndAttached: 'GIF generated and attached.',
    diagramGeneratedAndAttached: 'Diagram generated and attached.',
    textSaved: 'Text saved.',
    levelUpdated: 'Level updated.',
    contentRegeneratedByAi: 'Content regenerated by AI.',
    articleAddedToTheContextRag: 'Article added to the context (RAG). Click “Regenerate” to use it.',
    noImageGenerated: 'No image generated.',
    noGifGenerated: 'No GIF generated.',
    diagram: 'Diagram',
    diagramUpdated: 'Diagram updated.',
    diagramCreatedAndAttached: 'Diagram created and attached.',
    postContent: 'Post content',
    text: 'Text',
    writeThePostText: 'Write the post text…',
    level: 'Level',
    useMyDocumentsArticlesRag: 'Use my documents/articles (RAG)',
    webSearch: 'Web search',
    force: 'Force',
    generating: 'Generating…',
    regenerateWithAi: '✨ Regenerate with AI',
    addAnArticleSContext: 'Add an article\'s context',
    httpsArticleStudyPage: 'https://… (article, study, page)',
    adding: 'Adding…',
    addTheContext: 'Add the context',
    visualTemplate: 'Visual template',
    closeTheGallery: 'Close the gallery',
    chooseAVisualStyle: 'Choose a visual style',
    style: 'Style:',
    automatic: 'Automatic',
    generateAnImageByLevel: '🖼️ Generate an image (by level)',
    generateAnAnimatedGif: '🎞️ Generate an animated GIF',
    createEditADiagram: '📊 Create / edit a diagram',
    attachedDiagrams: 'Attached diagrams:',
    edit2: '✏️ Edit',
    closeTheDiagramGenerator: 'Close the diagram generator',
    generateADiagramExactTemplate: 'Generate a diagram (exact template)',
    postMedia: 'Post media',
    noMediaAddAnImageA: 'No media. Add an image, a video or a visual.',
    publishing: 'Publishing',
  },
  filters: {
    types: 'Types',
    reset: 'Reset',
    channel: 'Channel',
    status: 'Status',
  },
  miniMonth: {
    previousMonthMini: 'Previous month (mini)',
    nextMonthMini: 'Next month (mini)',
    goToToday: 'Go to today',
  },
  model: {
    toApprove: 'To approve',
    pending: 'Pending',
    approved: 'Approved',
    dismissed: 'Dismissed',
    published: 'Published',
    failed: 'Failed',
    publishing: 'Publishing',
    scheduledContent: 'Scheduled content',
    crmReminder: 'CRM reminder',
    emailCampaign: 'Email campaign',
    event: 'Event',
    scheduledPost: 'Scheduled post',
    adDelivery: 'Ad delivery',
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
    january: 'January',
    february: 'February',
    march: 'March',
    april: 'April',
    may: 'May',
    june: 'June',
    july: 'July',
    august: 'August',
    september: 'September',
    october: 'October',
    november: 'November',
    december: 'December',
    autoPublishing: 'Auto-publishing',
    appointment: 'Appointment',
    karexiaCalendar: 'Karexia — Calendar',
  },
  monthView: {
    addOnV: 'Add on {{v}}',
    vMore: '+{{v}} more',
  },
  quickCreateModal: {
    doNotRepeat: 'Do not repeat',
    everyDay: 'Every day',
    everyWeek: 'Every week',
    everyMonth: 'Every month',
    publishing: 'Publishing',
    reminder: 'Reminder',
    meeting: 'Meeting',
    scheduleAPost: 'Schedule a post',
    createAReminder: 'Create a reminder',
    scheduleAMeeting: 'Schedule a meeting',
    schedule: 'Schedule',
    createReminder: 'Create reminder',
    scheduleTheMeeting: 'Schedule the meeting',
    cancel: 'Cancel',
    creating: 'Creating…',
    eventType: 'Event type',
    the: 'The',
    platform: 'Platform',
    time: 'Time',
    titleOptional: 'Title (optional)',
    contentOptional: 'Content (optional)',
    postText: 'Post text…',
    mediaOptional: 'Media (optional)',
    noMediaAddAnImageOr: 'No media. Add an image or a video.',
    title: 'Title',
    eGDiscoveryCall: 'E.g. Discovery call',
    eGCallTheClientBack: 'E.g. Call the client back',
    date: 'Date',
    recurrence: 'Recurrence',
    createsAMeetingReminderInThe: 'Creates a meeting reminder in the CRM. Bookings with availability slots are managed from the booking pages (CRM → Scheduler).',
  },
  recommendationRail: {
    confidenceV: 'Confidence {{v}}%',
    recommendedSlots: 'Recommended slots',
    recommendedSlotsThemes: 'Recommended slots & themes',
    estimate: 'estimate',
    show: 'Show',
    hide: 'Hide',
    analyzingTheBestTimes: 'Analyzing the best times…',
    conflictsPostSAlreadyScheduledNear: '{{conflicts}} post(s) already scheduled near this slot',
    conflictsConflictS: '⚠ {{conflicts}} conflict(s)',
    useThisSlot: 'Use this slot',
  },
  timeGridView: {
    recommendedSlotTheme: 'Recommended slot — {{theme}}',
    recommendedSlotScheduleHere: 'Recommended slot — schedule here',
  },
};

export default en;
